import type { CategoryView, Role } from "@kopi/contracts";
import {
  type AUDIT_SORTS,
  type CATEGORY_SORTS,
  type DEVICE_SORTS,
  definedOnly,
  type EMPLOYEE_SORTS,
  MAX_PRODUCT_MODIFIER_GROUPS,
  type PRODUCT_SORTS,
  type SHIFT_SORTS,
  type SortValue,
} from "@kopi/contracts/client";
import type { AuditAction, ShiftStatus } from "@kopi/contracts/codes";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api, unwrap } from "./api";

export const meScopesQuery = queryOptions({
  queryKey: ["me", "scopes"],
  queryFn: async () => unwrap(await api.v1.me.scopes.get()),
  staleTime: 60_000,
  retry: false,
});

/** Page size of the infinite lists and pickers (the API's default). */
export const PAGE = 30;

/** Shared by every infinite list: start without a cursor, then follow `nextCursor`. */
const cursorPaging = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (page: { nextCursor: string | null }) => page.nextCursor ?? undefined,
};

/** The tenant brand (R1-09c) for the shell, the tab title and the favicon; any signed-in member. */
export const brandQuery = queryOptions({
  queryKey: ["brand"],
  queryFn: async () => unwrap(await api.v1.brand.get()),
  staleTime: 5 * 60_000,
});

/** The brand on the sign-in page: this register's tenant, or the defaults (no session needed). */
export const publicBrandQuery = queryOptions({
  queryKey: ["brand", "public"],
  queryFn: async () => unwrap(await api.v1.brand.public.get()),
  staleTime: 5 * 60_000,
  retry: false,
});

// Deliberately whole: a tenant's outlets are a small, bounded set (CODING_STANDARDS §4 exception).
export const outletsQuery = queryOptions({
  queryKey: ["outlets"],
  queryFn: async () => unwrap(await api.v1.outlets.get()),
});

export const mfaStatusQuery = queryOptions({
  queryKey: ["me", "mfa"],
  queryFn: async () => unwrap(await api.v1.me.mfa.get()),
});

export interface EmployeeFilters {
  q: string;
  active?: boolean | undefined;
  role?: Role | undefined;
  /** Undefined: the server's default order (by name). */
  sort?: SortValue<(typeof EMPLOYEE_SORTS)[number]> | undefined;
}

/** Prefix of every staff list query, for invalidation. */
export const employeesKey = ["employees"] as const;

export const employeesQuery = (filters: EmployeeFilters) =>
  infiniteQueryOptions({
    queryKey: [...employeesKey, filters],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.employees.get({
          query: { limit: PAGE, ...definedOnly({ cursor: pageParam, ...filters }) },
        }),
      ),
  });

/** Staff who can unlock this register; fails (401) when this browser isn't an enrolled register. */
export const registerStaffQuery = queryOptions({
  queryKey: ["devices", "current", "staff"],
  queryFn: async () => unwrap(await api.v1.devices.current.staff.get()),
  retry: false,
});

/** The tenant's legal entities, for the new-outlet form (owners only); a handful at most. */
export const legalEntitiesQuery = queryOptions({
  queryKey: ["legal-entities"],
  queryFn: async () => unwrap(await api.v1["legal-entities"].get()),
  staleTime: 5 * 60_000,
});

export const outletSettingsQuery = (outletId: string) =>
  queryOptions({
    queryKey: ["outlets", outletId, "settings"],
    queryFn: async () => unwrap(await api.v1.outlets({ outletId }).settings.get()),
  });

/** Prefix of every category query (list and picker), for invalidation. */
export const categoriesKey = ["catalogue", "categories"] as const;

/** Categories page by page: in menu order unless `sort` says otherwise. */
export const categoriesQuery = (sort?: SortValue<(typeof CATEGORY_SORTS)[number]>) =>
  infiniteQueryOptions({
    queryKey: [...categoriesKey, "list", sort],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.categories.get({ query: { limit: PAGE, ...definedOnly({ sort, cursor: pageParam }) } }),
      ),
  });

/**
 * One page of category choices for a server-side Combobox (search + paging), in menu order.
 * `activeOnly` limits it to categories new products may use.
 */
export const fetchCategoryChoices =
  (activeOnly = false) =>
  async (q: string, cursor: string | undefined) =>
    unwrap(
      await api.v1.categories.get({
        query: { limit: PAGE, ...definedOnly({ q, cursor, active: activeOnly ? true : undefined }) },
      }),
    );

/** A category as a choice, marked when inactive. */
export const categoryLabel = (c: CategoryView) => (c.active ? c.name : `${c.name} (nonaktif)`);

export interface ProductFilters {
  q: string;
  categoryId?: string | undefined;
  active?: boolean | undefined;
  /** Undefined: the server's default order (by name). */
  sort?: SortValue<(typeof PRODUCT_SORTS)[number]> | undefined;
}

/** Prefix of every product list query, for invalidation. */
export const productsKey = ["catalogue", "products"] as const;

export const productsQuery = (filters: ProductFilters) =>
  infiniteQueryOptions({
    queryKey: [...productsKey, "list", filters],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.products.get({
          query: { limit: PAGE, ...definedOnly({ cursor: pageParam, ...filters }) },
        }),
      ),
  });

export const productQuery = (productId: string) =>
  queryOptions({
    queryKey: ["catalogue", "products", productId],
    queryFn: async () => unwrap(await api.v1.products({ productId }).get()),
  });

export interface ModifierGroupFilters {
  q: string;
  active?: boolean | undefined;
}

/** Prefix of every modifier group query (list, picker and detail), for invalidation. */
export const modifierGroupsKey = ["catalogue", "modifier-groups"] as const;

export const modifierGroupsQuery = (filters: ModifierGroupFilters) =>
  infiniteQueryOptions({
    queryKey: [...modifierGroupsKey, "list", filters],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1["modifier-groups"].get({
          query: { limit: PAGE, ...definedOnly({ cursor: pageParam, ...filters }) },
        }),
      ),
  });

export const modifierGroupQuery = (groupId: string) =>
  queryOptions({
    queryKey: [...modifierGroupsKey, groupId],
    queryFn: async () => unwrap(await api.v1["modifier-groups"]({ groupId }).get()),
  });

/**
 * Every group a product offers, with their options: one request for up to
 * `MAX_PRODUCT_MODIFIER_GROUPS`, the most a product can have, so nothing is left on a later page.
 */
export const productModifierGroupsQuery = (productId: string) =>
  queryOptions({
    queryKey: [...modifierGroupsKey, "product", productId],
    queryFn: async () =>
      unwrap(
        await api.v1["modifier-groups"].get({ query: { limit: MAX_PRODUCT_MODIFIER_GROUPS, productId } }),
      ).items,
  });

/** One page of active modifier groups for a server-side Combobox (search + paging), by name. */
export const fetchModifierGroupChoices = async (q: string, cursor: string | undefined) =>
  unwrap(
    await api.v1["modifier-groups"].get({
      query: { limit: PAGE, active: true, ...definedOnly({ q, cursor }) },
    }),
  );

/** The system units (R1-13): a small fixed set that never changes while the app runs. */
export const unitsQuery = queryOptions({
  queryKey: ["catalogue", "units"],
  queryFn: async () => unwrap(await api.v1.units.get()).items,
  staleTime: Number.POSITIVE_INFINITY,
});

export interface IngredientFilters {
  q: string;
  active?: boolean | undefined;
}

/** Prefix of every ingredient query (list and picker), for invalidation. */
export const ingredientsKey = ["catalogue", "ingredients"] as const;

export const ingredientsQuery = (filters: IngredientFilters) =>
  infiniteQueryOptions({
    queryKey: [...ingredientsKey, "list", filters],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.ingredients.get({
          query: { limit: PAGE, ...definedOnly({ cursor: pageParam, ...filters }) },
        }),
      ),
  });

/** One page of active ingredients for a server-side Combobox (search + paging), by name. */
export const fetchIngredientChoices = async (q: string, cursor: string | undefined) =>
  unwrap(
    await api.v1.ingredients.get({ query: { limit: PAGE, active: true, ...definedOnly({ q, cursor }) } }),
  );

/** Prefix of a variant's recipe history, for invalidation after a new version. */
export const recipeVersionsKey = (variantId: string) => ["catalogue", "recipe-versions", variantId] as const;

/** A variant's recipe versions, latest (the one in use) first, page by page. */
export const recipeVersionsQuery = (variantId: string) =>
  infiniteQueryOptions({
    queryKey: recipeVersionsKey(variantId),
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1
          .variants({ variantId })
          ["recipe-versions"].get({ query: { limit: 10, ...definedOnly({ cursor: pageParam }) } }),
      ),
  });

/** A variant's price history at an outlet, latest start first; each page also carries `current`. */
export const pricesQuery = (outletId: string, variantId: string) =>
  infiniteQueryOptions({
    queryKey: ["outlets", outletId, "prices", variantId],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1
          .outlets({ outletId })
          .prices.get({ query: { variantId, limit: PAGE, ...definedOnly({ cursor: pageParam }) } }),
      ),
    enabled: Boolean(outletId && variantId),
  });

/** Prefix of an outlet's device list queries, for invalidation. */
export const devicesKey = (outletId: string | undefined) => ["outlets", outletId, "devices"] as const;

/** An outlet's enrollments (active and revoked), page by page; newest first unless `sort` says otherwise. */
export const devicesQuery = (outletId: string, sort?: SortValue<(typeof DEVICE_SORTS)[number]>) =>
  infiniteQueryOptions({
    queryKey: [...devicesKey(outletId), { sort }],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId }).devices.get({
          query: { limit: PAGE, status: "all", ...definedOnly({ cursor: pageParam, sort }) },
        }),
      ),
    enabled: Boolean(outletId),
  });

/**
 * What the outlet's register sells now (R1-09), whole by design. Refetched every minute and just after
 * a scheduled price takes effect (`nextChangeAt`); the browser revalidates with the ETag, so an
 * unchanged catalogue costs a 304.
 */
export const catalogueSnapshotQuery = (outletId: string) =>
  queryOptions({
    queryKey: ["outlets", outletId, "catalogue"],
    queryFn: async () => unwrap(await api.v1.outlets({ outletId }).catalogue.get()),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const next = query.state.data?.nextChangeAt;
      const untilChange = next ? Date.parse(next) - Date.now() + 1_000 : Number.POSITIVE_INFINITY;
      return Math.max(5_000, Math.min(60_000, untilChange));
    },
  });

/** The lease bound to this register, if its manager has approved offline writing. */
export const offlineLeaseQuery = (outletId: string) =>
  queryOptions({
    queryKey: ["outlets", outletId, "offline-lease"],
    queryFn: async () => unwrap(await api.v1.outlets({ outletId })["offline-lease"].get()),
    enabled: Boolean(outletId),
    retry: false,
    staleTime: 30_000,
  });

export const availabilityQuery = (outletId: string, productId: string) =>
  queryOptions({
    queryKey: ["outlets", outletId, "availability", productId],
    queryFn: async () =>
      unwrap(await api.v1.outlets({ outletId }).products({ productId }).availability.get()),
    enabled: Boolean(outletId),
  });

export interface AuditFilters {
  action: AuditAction | "";
  /** Local days (`YYYY-MM-DD`) from the date pickers; blank for no bound. */
  from: string;
  to: string;
  /** Undefined: newest first. */
  sort?: SortValue<(typeof AUDIT_SORTS)[number]> | undefined;
}

/**
 * The audit log, newest first unless `sort` says otherwise; local days become ISO instants (start of
 * `from`, end of `to`). `limit` is the page size (the audit screen pages by 10).
 */
export const auditEventsQuery = (filters: AuditFilters, limit = PAGE) =>
  infiniteQueryOptions({
    queryKey: ["audit-events", filters, limit],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1["audit-events"].get({
          query: {
            limit,
            ...definedOnly({
              cursor: pageParam,
              action: filters.action || undefined,
              from: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
              to: filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
              sort: filters.sort,
            }),
          },
        }),
      ),
  });

/** Prefix of every shift query (current, list and detail), for invalidation. */
export const shiftsKey = ["shifts"] as const;

/**
 * This register's open or closing shift (R1-15), for the top bar and the shift screen; only asked on an
 * enrolled register, where shifts are worked.
 */
export const currentShiftQuery = queryOptions({
  queryKey: [...shiftsKey, "current"],
  queryFn: async () => unwrap(await api.v1.shifts.current.get()),
  staleTime: 30_000,
});

export const shiftQuery = (shiftId: string) =>
  queryOptions({
    queryKey: [...shiftsKey, shiftId],
    queryFn: async () => unwrap(await api.v1.shifts({ shiftId }).get()),
  });

/** An outlet's shifts, latest opened first unless `sort` says otherwise; `status` narrows them. */
export const outletShiftsQuery = (
  outletId: string,
  status?: ShiftStatus | undefined,
  sort?: SortValue<(typeof SHIFT_SORTS)[number]> | undefined,
) =>
  infiniteQueryOptions({
    queryKey: [...shiftsKey, "outlet", outletId, { status, sort }],
    ...cursorPaging,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.v1.outlets({ outletId }).shifts.get({
          query: { limit: PAGE, ...definedOnly({ cursor: pageParam, status, sort }) },
        }),
      ),
    enabled: Boolean(outletId),
  });
